import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Box,
  useTheme,
  Button,
  TextField,
} from '@mui/material';
import {
  AddShoppingCart,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { Product } from '../../api/storeApi';
import { useCart } from '../../hooks/useCart';

interface ProductCardProps {
  product: Product;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addItem, removeItem, updateItemQuantity, items } = useCart();
  const theme = useTheme();

  // Get quantity of this product in cart
  const cartItem = items.find(item => item.id === product.id);
  const quantity = cartItem ? cartItem.quantity : 0;

  // Get banner image or fallback to first image
  // Images are at the top level, not inside product object
  // console.log(`Product ${product.name} images:`, product.images);
  
  // Use images array directly from product object
  
  const productImages = product.images || [];
  
  // Find banner image
  const bannerImage = productImages.find(img => img && img.is_banner === true);
  
  // Find first image if no banner image
  const firstImage = productImages[0];
  
  // Determine which image URL to use - using a static placeholder instead of random Unsplash
  let imageUrl = 'https://placehold.co/400x300?text=No+Image';
  
  if (bannerImage && bannerImage.image) {
    imageUrl = bannerImage.image;
  } else if (firstImage && firstImage.image) {
    imageUrl = firstImage.image;
  }
  
  const displayImage = imageUrl;

  const handleAddToCart = () => {
    addItem(product);
  };

  const handleRemoveFromCart = () => {
    if (quantity > 1) {
      updateItemQuantity(product.id, quantity - 1);
    } else {
      removeItem(product.id);
    }
  };

  const [inputQty, setInputQty] = React.useState<number | ''>(quantity);
  React.useEffect(() => { setInputQty(quantity); }, [quantity]);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <Box
        sx={{
          minWidth: { xs: '130px', sm: 'unset' }, // Decrease card size for mobile
          width: '100%',
        }}
      >
        {/* Product Image */}
        <Box sx={{ position: 'relative', pt: { xs: '75%', sm: '60%' } /* Taller aspect ratio on mobile */ }}>
          <CardMedia
            component="img"
            image={displayImage}
            alt={product.name}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={(e) => {
              // No need to log errors repeatedly
              // Set fallback if image fails to load
              const target = e.target as HTMLImageElement;
              target.onerror = null; // Prevent infinite error loop
              target.src = 'https://placehold.co/400x300?text=No+Image';
            }}
          />
        </Box>

        <CardContent sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: { xs: 1, sm: 1.5 }, // Less padding on mobile
        }}>
          {/* Brand and Product Code */}
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mb: { xs: 0.5, sm: 1 }, // Less margin on mobile
              fontSize: { xs: '0.75rem', sm: '0.875rem' } // Smaller font on mobile
            }}
          >
          </Typography>

          {/* Product Name */}
          <Typography
            gutterBottom
            variant="h6"
            component="h2"
            sx={{
              minHeight: '2.4em', // Reserve space for two lines
              maxHeight: '2.4em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: '1.2em',
              mb: 0, 
              fontSize: { xs: '0.875rem', sm: '1rem' } 
            }}
          >
            {product.name}
          </Typography>

          {/* Price and Add to Cart */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 0, 
              gap: 0.2 
            }}
          >
            <Box>
              <Typography 
                variant="h6" 
                color="primary" 
                sx={{ 
                  fontWeight: 600, 
                  fontSize: { xs: '0.9rem', sm: '1.1rem' } // Smaller font on mobile
                }}
              >
                ₹{product.retailer_rate.toFixed(2)}
              </Typography>
            </Box>
            {/* Extra compact cart controls for mobile */}
            <Box
              sx={{
                height: { xs: 24, sm: 36 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                gap: { xs: 0.5, sm: 1 },
              }}
            >
              {quantity > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: { xs: 24, sm: 36 },
                    gap: 0,
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: 2,
                    background: '#fff',
                    boxSizing: 'border-box',
                    px: 0.5,
                  }}
                >
                  <Button
                    size="small"
                    sx={{
                      minWidth: { xs: 18, sm: 24 },
                      height: { xs: 18, sm: 24 },
                      px: 0,
                      fontSize: { xs: '0.7rem', sm: '0.85rem' },
                      lineHeight: 1,
                      m: 0,
                    }}
                    onClick={handleRemoveFromCart}
                  >
                    <RemoveIcon sx={{ fontSize: { xs: 12, sm: 18 } }} />
                  </Button>
                  <TextField
                    type="number"
                    value={inputQty}
                    onChange={e => {
                      const v = e.target.value;
                      if (/^\d*$/.test(v)) setInputQty(v === '' ? '' : Math.max(1, parseInt(v)));
                    }}
                    onBlur={() => {
                      if (!inputQty || inputQty < 1) {
                        setInputQty(quantity);
                      } else if (inputQty !== quantity) {
                        updateItemQuantity(product.id, Number(inputQty));
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    inputProps={{
                      min: 1,
                      style: {
                        textAlign: 'center',
                        width: '1.3em',
                        height: '0.9em',
                        padding: 0,
                        fontSize: '0.7rem',
                        MozAppearance: 'textfield',
                        background: 'transparent',
                        border: 'none',
                      },
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                    sx={{
                      mx: { xs: 0.1, sm: 0.3 },
                      width: { xs: 16, sm: 28 },
                      minWidth: { xs: 16, sm: 28 },
                      background: 'transparent',
                      '& .MuiInputBase-root': {
                        height: { xs: 16, sm: 28 },
                        minHeight: { xs: 16, sm: 28 },
                        display: 'flex',
                        alignItems: 'center',
                        background: 'transparent',
                      },
                      '& .MuiInputBase-input': {
                        p: 0,
                        height: { xs: 16, sm: 28 },
                        fontSize: { xs: '0.7rem', sm: '0.85rem' },
                        textAlign: 'center',
                        background: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 'none',
                      },
                      '&:before, &:after, & .MuiInput-underline:before, & .MuiInput-underline:after': {
                        borderBottom: 'none !important',
                      },
                      '& input[type=number]': {
                        MozAppearance: 'textfield',
                      },
                      '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    }}
                    variant="standard"
                    size="small"
                  />
                  <Button
                    size="small"
                    sx={{
                      minWidth: { xs: 18, sm: 24 },
                      height: { xs: 18, sm: 24 },
                      px: 0,
                      fontSize: { xs: '0.7rem', sm: '0.85rem' },
                      lineHeight: 1,
                      m: 0,
                    }}
                    onClick={handleAddToCart}
                  >
                    <AddIcon sx={{ fontSize: { xs: 12, sm: 18 } }} />
                  </Button>
                </Box>
              ) : (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleAddToCart}
                  aria-label="Add to cart"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      transform: 'scale(1.1)',
                    },
                    height: { xs: 24, sm: 36 },
                    width: { xs: 24, sm: 36 },
                    p: 0,
                    fontSize: { xs: '0.85rem', sm: '1rem' },
                  }}
                >
                  <AddShoppingCart sx={{ fontSize: { xs: 14, sm: 20 } }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </CardContent>
      </Box>
    </Card>
  );
};
